import { v4 as uuidv4 } from 'uuid';
import { getProcessEnvUrl } from 'utils/getUrl';
import { AuthError } from './error';

export class OAuthClient {
  private readonly env = {
    root: () => getProcessEnvUrl('GATEWAY_URL') as string,
    realm: () => getProcessEnvUrl('KEYCLOAK_REALM') as string,
    oauth: () => `${getProcessEnvUrl('GATEWAY_URL')}/oauth2` as const,
  };

  private static isUrlOrPath(url: string) {
    return url.startsWith('http');
  }

  /**
   * URL that initiates Keycloak auto-login flow.
   * @returns The Keycloak auto-login URL.
   */
  private createLoginLink() {
    return `${this.env.root()}/auth/realms/${this.env.realm()}/login-link` as const;
  }

  async login(payload: {
    email: string;
    password: string;
    clientUUID: string;
    callbackUrl: string;
  }) {
    const res = await fetch(this.createLoginLink(), {
      method: 'post',
      mode: 'cors',
      // credentials is in default same-origin
      credentials: 'omit',
      body: JSON.stringify({
        username: payload.email,
        userUuid: payload.clientUUID,
        password: payload.password,
        clientId: 'oauth2-proxy',
        redirectUrl: this.createAuthUrl(payload.callbackUrl),
        nonce: uuidv4(),
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      if (res.status === 401) {
        throw new AuthError({ type: 'no-access' });
      }
      throw new AuthError({ type: 'unknown-error', error: res });
    }

    const json = await res.json();
    return json;
  }

  /**
   * Creates an OAuth sign-in URL.
   *
   * The resulting URL will contain the `rd` query parameter which the backend
   * uses to redirect the user after a successful login.
   *
   * @param redirect - The path to redirect the user to after a successful login like redirect = '/login/confirm' or redirect = 'https://www.example.com'.
   * @returns The OAuth sign-in URL.
   */
  createAuthUrl(redirect: string) {
    const redirectUrl = OAuthClient.isUrlOrPath(redirect)
      ? redirect
      : new URL(
          `${window.location.protocol}//${window.location.host}${redirect}`,
        ).toString();

    const url = new URL(`${this.env.oauth()}/sign_in`);
    url.searchParams.set('rd', redirectUrl);

    return url.toString();
  }

  /**
   * Creates an OAuth sign-out URL.
   *
   * @param redirect - The path to redirect the user to after a successful logout like redirect = '/login' or redirect = 'https://www.example.com'.
   * @returns The OAuth sign-out URL.
   */
  createLogoutUrl(redirect: string) {
    const redirectUrl = OAuthClient.isUrlOrPath(redirect)
      ? redirect
      : new URL(
          `${window.location.protocol}//${window.location.host}${redirect}`,
        ).toString();

    const url = new URL(`${this.env.oauth()}/sign_out`);
    url.searchParams.set('rd', redirectUrl);

    return url.toString();
  }

  async fetchAuthStatus() {
    const response = await fetch(`${this.env.oauth()}/auth`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-forwarded-uri': '/webapi/client',
      },
    });

    if (!response.ok) {
      return false;
    }

    return true;
  }
}
